import type { Finding } from "./types";

/** What `word.docreview.select_anchor` needs to find a finding by text when
 *  its durable anchor is gone (AIVA-8209). Mirrors the service layer's
 *  `FindingLocationHint`. */
export interface FindingLocation {
  matchText?: string;
  occurrenceIndex?: number;
  expected?: string;
}

/**
 * The text-level location a finding carries on its own `fix_params` — the
 * same `matchText`/`occurrenceIndex` the anchor step originally searched
 * for, and the only route back to the location once the anchor no longer
 * resolves.
 *
 * `fix_params` is an untyped bag on the wire (`Record<string, unknown>`), so
 * each field is validated rather than asserted: a finding whose params
 * carry no usable `matchText` has no text location at all, which is a
 * different thing from one whose text simply moved.
 */
export function findingLocation(finding: Finding): FindingLocation {
  const params = finding.fix_params as { matchText?: unknown; occurrenceIndex?: unknown };
  const matchText = typeof params.matchText === "string" && params.matchText.trim() ? params.matchText : undefined;
  const occurrenceIndex = typeof params.occurrenceIndex === "number" ? params.occurrenceIndex : undefined;
  return { matchText, occurrenceIndex, expected: finding.expected ?? undefined };
}

/** Whether this finding can be navigated to at all: either its anchor is
 *  still recorded, or its own text can be searched for. Only a finding with
 *  neither is genuinely unreachable — and that is the one case the card must
 *  present as inert rather than silently swallowing the click. */
export function isLocatable(finding: Finding): boolean {
  return Boolean(finding.anchor_tag) || findingLocation(finding).matchText !== undefined;
}
